import { useCallback, useEffect, useRef } from 'react';
import size from '~/assets/icons/size.svg';
import tireBig from '~/assets/icons/tire_icon_big.svg';
import tireLow from '~/assets/icons/tire_icon_low.svg';
import tireRegular from '~/assets/icons/tire_icon_regular.svg';
import tireSize from '~/assets/icons/tire_size.svg';
import wheelCollapse from '~/assets/icons/wheel_collapse.svg';
import wheelDeflection from '~/assets/icons/wheel_deflection.svg';
import wheelSettingsIcon from '~/assets/icons/wheel_settings.svg';
import { playSound, Sound } from '~/assets/sounds';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import {
	PropertyContainer,
	PropertyContainerRow,
} from '~/components/ui/PropertyContainer';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import {
	IVehicleWheelsSettings,
	ModalButtonColor,
	ModalType,
	VehicleWheelsSetting,
	VehicleWheelTireProfile,
} from '~/shared';
import {
	setTireProfile,
	setWheelSettings,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledContainer,
	StyledSettingsContainer,
	StyledSettingsItemContainer,
	StyledSettingsItemTitleContainer,
	StyledWheelSettingsItem,
	StyledWheelSizeContainer,
} from './WheelSettings.styled';

const WheelSettings = () => {
	const dispatch = useLayoutDispatch();
	const ownedWheelSettings = useObservable(
		rage.vehicleTuningShop.getOwnedWheelsSettings(),
	);
	const wheelSettingsRanges = useObservable(
		rage.vehicleTuningShop.getWheelsSettingsRanges(),
	);
	const wheelSettings = useLayoutSelector(
		(state) => state.tuningStudio.wheelSettings,
	);
	const wheelTireProfile = useObservable(
		rage.vehicleTuningShop.getWheelTireProfile(),
	);
	const isInitializedRef = useRef(false);

	const selectedTireProfile = useLayoutSelector(
		(state) => state.tuningStudio.tireProfile,
	);

	const handleTireProfileClick = useCallback(
		async (profile: VehicleWheelTireProfile) => {
			playSound(Sound.ClickButton);

			if (profile === selectedTireProfile) {
				return;
			}

			dispatch(setTireProfile(profile));
			await rage.vehicleTuningShop.setTiresProfilePreview(profile);
		},
		[selectedTireProfile],
	);

	const handleChange = useCallback(
		(field: VehicleWheelsSetting, value: number) => {
			dispatch(
				setWheelSettings({
					...wheelSettings,
					[field]: value,
				}),
			);
		},
		[wheelSettings],
	);

	const handleResetModal = useCallback(
		(name: string, fields: VehicleWheelsSetting[]) => {
			if (!ownedWheelSettings) return;

			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: `Вы действительно хотите сбросить раздел "${name}"?`,
					acceptButton: { text: 'Сбросить', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				(result) => {
					if (result.isAccepted) {
						const updatedWheelSettings = fields.reduce(
							(acc: IVehicleWheelsSettings, field) => {
								const key = field as keyof IVehicleWheelsSettings;
								acc[key] = ownedWheelSettings[key];
								return acc;
							},
							{ ...wheelSettings } as IVehicleWheelsSettings,
						);

						dispatch(setWheelSettings(updatedWheelSettings));
					}
				},
			);
		},
		[wheelSettings, ownedWheelSettings],
	);

	useEffect(() => {
		if (ownedWheelSettings) {
			dispatch(setWheelSettings(ownedWheelSettings));
			isInitializedRef.current = true;
		}
	}, [ownedWheelSettings]);

	useEffect(() => {
		if (wheelTireProfile) {
			dispatch(setTireProfile(wheelTireProfile));
		}
	}, [wheelTireProfile]);

	useEffect(() => {
		if (!ownedWheelSettings || !isInitializedRef.current) {
			return;
		}
		rage.vehicleTuningShop.setWheelsSettings(wheelSettings);
	}, [wheelSettings, ownedWheelSettings]);

	if (!ownedWheelSettings || !wheelSettingsRanges) {
		return null;
	}

	return (
		<StyledContainer>
			<SectionHeading
				text="Настройка колес"
				icon={wheelSettingsIcon}
				textOpacity={1}
				iconOpacity={1}
				fontWeight={500}
			/>
			<Divider
				$direction="H"
				$color="#ffffff"
				style={{ marginTop: 20, marginBottom: 20, opacity: 0.1 }}
			/>
			<StyledWheelSizeContainer>
				<StyledWheelSettingsItem
					$active={selectedTireProfile === VehicleWheelTireProfile.LOW}
					onClick={() => handleTireProfileClick(VehicleWheelTireProfile.LOW)}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={tireLow} />
					<div>
						<Text>Профиль</Text>
						<Text>Низкий</Text>
					</div>
				</StyledWheelSettingsItem>
				<StyledWheelSettingsItem
					$active={selectedTireProfile === VehicleWheelTireProfile.MEDIUM}
					onClick={() => handleTireProfileClick(VehicleWheelTireProfile.MEDIUM)}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={tireRegular} />
					<div>
						<Text>Профиль</Text>
						<Text>Средний</Text>
					</div>
				</StyledWheelSettingsItem>
				<StyledWheelSettingsItem
					$active={selectedTireProfile === VehicleWheelTireProfile.HIGH}
					onClick={() => handleTireProfileClick(VehicleWheelTireProfile.HIGH)}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={tireBig} />
					<div>
						<Text>Профиль</Text>
						<Text>Высокий</Text>
					</div>
				</StyledWheelSettingsItem>
			</StyledWheelSizeContainer>
			<ScrollContainer height={310} style={{ marginTop: 30 }}>
				<StyledSettingsContainer>
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Развал колес'} icon={wheelCollapse} />
							<Button
								color={ButtonColors.Default}
								height={ButtonHeight.Height20}
								style={{ width: '65px' }}
								borderRadius={ButtonBorderRadius.Radius6}
								onClick={() =>
									handleResetModal('Развал колес', [
										VehicleWheelsSetting.FRONT_CAMBER,
										VehicleWheelsSetting.REAR_CAMBER,
									])
								}
							>
								<Text>Сбросить</Text>
							</Button>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Передние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.FRONT_CAMBER]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.FRONT_CAMBER, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_CAMBER][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_CAMBER][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
								<PropertyContainerRow>
									<Text>Задние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.REAR_CAMBER]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.REAR_CAMBER, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_CAMBER][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_CAMBER][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Вылет колес'} icon={wheelDeflection} />
							<Button
								color={ButtonColors.Default}
								height={ButtonHeight.Height20}
								style={{ width: '65px' }}
								borderRadius={ButtonBorderRadius.Radius6}
								onClick={() =>
									handleResetModal('Вылет колес', [
										VehicleWheelsSetting.FRONT_OFFSET,
										VehicleWheelsSetting.REAR_OFFSET,
									])
								}
								onMouseEnter={() => playSound(Sound.LinkCursor)}
							>
								<Text>Сбросить</Text>
							</Button>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Передние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.FRONT_OFFSET]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.FRONT_OFFSET, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_OFFSET][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_OFFSET][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
								<PropertyContainerRow>
									<Text>Задние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.REAR_OFFSET]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.REAR_OFFSET, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_OFFSET][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_OFFSET][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Ширина колес'} icon={tireSize} />
							<Button
								color={ButtonColors.Default}
								height={ButtonHeight.Height20}
								style={{ width: '65px' }}
								borderRadius={ButtonBorderRadius.Radius6}
								onClick={() =>
									handleResetModal('Ширина колес', [VehicleWheelsSetting.WIDTH])
								}
							>
								<Text>Сбросить</Text>
							</Button>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Ширина</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.WIDTH]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.WIDTH, value);
										}}
										min={wheelSettingsRanges[VehicleWheelsSetting.WIDTH][0]}
										max={wheelSettingsRanges[VehicleWheelsSetting.WIDTH][1]}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Размер колес'} icon={size} />
							<Button
								color={ButtonColors.Default}
								height={ButtonHeight.Height20}
								style={{ width: '65px' }}
								borderRadius={ButtonBorderRadius.Radius6}
								onClick={() =>
									handleResetModal('Размер колес', [
										VehicleWheelsSetting.RADIUS,
									])
								}
								onMouseEnter={() => playSound(Sound.LinkCursor)}
							>
								<Text>Сбросить</Text>
							</Button>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Размер</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.RADIUS]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.RADIUS, value);
										}}
										min={wheelSettingsRanges[VehicleWheelsSetting.RADIUS][0]}
										max={wheelSettingsRanges[VehicleWheelsSetting.RADIUS][1]}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
				</StyledSettingsContainer>
			</ScrollContainer>
		</StyledContainer>
	);
};

export { WheelSettings };
